import config from "@/config"
import { isComingSoon } from "@/core/launch"
import { buildCanonicalUrl } from "@/core/site"

import { LOCALES, type Locale } from "../i18n/locale"
import { buildHreflangLinks, type LocaleVersion } from "./hreflang"
import { type JsonLdObject, serializeJsonLd } from "./json-ld"
import { ZH_PAGE_PATHS } from "./prerender"

export const TITLE_MAX = 60
export const DESCRIPTION_MIN = 120
export const DESCRIPTION_MAX = 155
/** Chinese packs about twice the meaning into a character: tighter limits (scripts/check-marketing-pages.mjs mirrors them). */
export const ZH_TITLE_MAX = 30
export const ZH_DESCRIPTION_MIN = 50
export const ZH_DESCRIPTION_MAX = 100

/** Versions of an English path that exist: English always, Chinese when the page is translated. */
export function pageVersions(path: string): LocaleVersion[] {
  const en = { hreflang: LOCALES.en.hreflang, localePrefix: LOCALES.en.prefix }
  const zh = { hreflang: LOCALES.zh.hreflang, localePrefix: LOCALES.zh.prefix }
  return ZH_PAGE_PATHS.includes(path) ? [en, zh] : [en]
}

export interface SeoInput {
  /** Topic first; the brand is appended. */
  title: string
  description: string
  /**
   * Root-relative path of this page, without locale prefix. Must equal the route pathname: root.tsx
   * also emits a canonical from the pathname, and the router dedupes links only when identical.
   */
  path: string
  /** Locale prefix of this page: "" for English, "zh" for Chinese. */
  localePrefix?: string
  type?: "website" | "article"
  /** Root-relative or absolute image URL for Open Graph. */
  image?: string
  noindex?: boolean
  /** Versions that really exist; default `pageVersions(path)`. hreflang is emitted only for two or more. */
  versions?: readonly LocaleVersion[]
  jsonLd?: readonly JsonLdObject[]
}

export interface SeoHead {
  meta: Record<string, string>[]
  links: Record<string, string>[]
  scripts: { type: string; children: string }[]
  /** Length-rule violations; routes' tests assert this is empty. */
  problems: string[]
}

/** Full `<title>` for a page: "Topic | Brand". */
export function buildTitle(title: string): string {
  return `${title} | ${config.core.projectName}`
}

/**
 * Shared `head()` result for marketing routes: title, description, self-referential canonical,
 * Open Graph/Twitter, optional noindex, hreflang and JSON-LD (TanStack `head()` accepts `scripts`).
 */
export function buildHead(input: SeoInput): SeoHead {
  const locale: Locale = input.localePrefix === LOCALES.zh.prefix ? "zh" : "en"
  const title = buildTitle(input.title)
  const [titleMax, descMin, descMax] =
    locale === "zh"
      ? [ZH_TITLE_MAX, ZH_DESCRIPTION_MIN, ZH_DESCRIPTION_MAX]
      : [TITLE_MAX, DESCRIPTION_MIN, DESCRIPTION_MAX]
  const url = buildCanonicalUrl(input.path, input.localePrefix)
  const image = input.image ? new URL(input.image, config.core.websiteUrl).href : undefined

  const problems: string[] = []
  if (title.length > titleMax) problems.push(`title is ${title.length} chars (max ${titleMax})`)
  const d = input.description.length
  if (d < descMin || d > descMax) {
    problems.push(`description is ${d} chars (${descMin}-${descMax})`)
  }

  return {
    meta: [
      { title },
      { name: "description", content: input.description },
      ...(input.noindex || isComingSoon() ? [{ name: "robots", content: "noindex" }] : []),
      { property: "og:type", content: input.type ?? "website" },
      { property: "og:site_name", content: config.core.projectName },
      { property: "og:locale", content: LOCALES[locale].ogLocale },
      { property: "og:title", content: title },
      { property: "og:description", content: input.description },
      { property: "og:url", content: url },
      ...(image ? [{ property: "og:image", content: image }] : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: input.description },
      ...(image ? [{ name: "twitter:image", content: image }] : []),
    ],
    links: [
      { rel: "canonical", href: url },
      ...buildHreflangLinks(input.path, input.versions ?? pageVersions(input.path)),
    ],
    scripts: (input.jsonLd ?? []).map((data) => ({
      type: "application/ld+json",
      children: serializeJsonLd(data),
    })),
    problems,
  }
}
